import { Link } from '@tanstack/react-router';
import type { Sport } from '@painpal/contracts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SportFilter, ZoneSource } from '../../api/keys';
import { useWeeklyZones } from '../../api/queries';
import { EChart } from '../../components/EChart';
import { EmptyState, ErrorPanel, LoadingBlock } from '../../components/StateViews';
import { ToggleTabs } from '../../components/ToggleTabs';
import { rangeStart, RANGE_LABEL, type FitnessRange } from '../../lib/dates';
import { itemAt } from '../../lib/zones';
import { useVariant, type Variant } from '../../ui/useMediaQuery';
import { onlyLabel, zoneSources } from './sportFilterModel';
import { WEEKLY_HEIGHT, WEEKLY_LABEL, weekPx, weeklyView, weeklyZonesOption, type WeeklyView } from './timeInZoneModel';
import fitness from './fitness.module.css';
import styles from './timeInZone.module.css';

const SOURCE_LABEL: Record<ZoneSource, string> = { power: 'POWER', hr: 'HR', pace: 'PACE' };

interface WeeklyChartProps { view: WeeklyView; variant: Variant; shownKey: string | null; drawIn: boolean }

function WeeklyChart({ view, variant, shownKey, drawIn }: WeeklyChartProps) {
  const option = useMemo(() => weeklyZonesOption(view, variant), [view, variant]);
  // Start at the newest week; the scroll stays inside the card. Keyed on the range, source and
  // variant the weeks are drawn for, so a switch re-runs it but a live refetch keeps the user's scroll.
  const scrollToLatest = useCallback((node: HTMLDivElement | null): void => {
    if (node !== null && shownKey !== null) node.scrollLeft = node.scrollWidth;
  }, [shownKey, variant]);
  return (
    <>
      <div className={styles.scroll} ref={scrollToLatest}>
        <div style={{ minWidth: view.rows.length * weekPx(variant) }}>
          <EChart option={option} height={WEEKLY_HEIGHT} label={WEEKLY_LABEL} drawIn={drawIn} />
        </div>
      </div>
      <ul className={styles.key} aria-label="Zone key">
        {view.names.map((name, z) => (
          <li key={name}><span className={styles.swatch} style={{ background: itemAt(view.colors, z) }} aria-hidden="true" />{name}</li>
        ))}
      </ul>
    </>
  );
}

interface TimeInZoneProps { range: FitnessRange; today: string; poll: () => number | false; sport: SportFilter; sports: readonly Sport[] }

export function TimeInZoneCard({ range, today, poll, sport, sports }: TimeInZoneProps) {
  const sources = zoneSources(sport, sports);
  const [chosen, setSource] = useState<ZoneSource>(sources[0]);
  // A filter that doesn't offer the chosen source (POWER, then RUN) falls back to its first one.
  const source = sources.includes(chosen) ? chosen : sources[0];
  const variant = useVariant();
  const from = rangeStart(range, today);
  const weekly = useWeeklyZones(from, today, source, sport, poll);
  // Placeholder weeks belong to the previous range, so they have no key of their own yet.
  const shownKey = weekly.isPlaceholderData ? null : `${from ?? 'all'}|${source}|${sport}`;
  const view = useMemo(() => (weekly.data === undefined ? null : weeklyView(weekly.data, source)), [weekly.data, source]);
  // A source switch unmounts the chart behind the loading block; only the card's first chart may draw in.
  const [hasDrawn, setHasDrawn] = useState(false);
  useEffect(() => {
    if (view !== null) setHasDrawn(true);
  }, [view]);
  const options = sources.map((value) => ({ value, label: SOURCE_LABEL[value] }));
  return (
    <section className={fitness.panel} aria-labelledby="time-in-zone-title">
      <header className={fitness.chartHeader}>
        <div className={fitness.chartHeadLeft}>
          <p className={fitness.chartEyebrow}>{RANGE_LABEL[range]} · WEEKLY{sport === 'all' ? '' : ` · ${onlyLabel(sport)}`}</p>
          <h2 id="time-in-zone-title" className={fitness.chartTitle}>TIME IN ZONE</h2>
        </div>
        {options.length > 1 && <ToggleTabs label="Zone source" options={options} value={source} onChange={setSource} />}
      </header>
      {weekly.isPending && <LoadingBlock label="LOADING TIME IN ZONE" height={WEEKLY_HEIGHT} />}
      {weekly.isError && <ErrorPanel error={weekly.error} onRetry={() => void weekly.refetch()} />}
      {weekly.isSuccess && view === null && (
        <EmptyState title={`NO ${SOURCE_LABEL[source]} ZONES IN THIS RANGE`} body="Zones appear for activities whose date has a threshold set.">
          <Link to="/settings" className="btn btn-outline">SET THRESHOLDS →</Link>
        </EmptyState>
      )}
      {view !== null && <WeeklyChart view={view} variant={variant} shownKey={shownKey} drawIn={!hasDrawn} />}
    </section>
  );
}
